import React from 'react';
import { Link } from 'react-router-dom';
import {
  CpuChipIcon,
  BuildingLibraryIcon,
  ArrowTopRightOnSquareIcon,
} from 'components/icons';
import PageHeader from '../../components/ui/PageHeader';

// =============================================================================
// Integrations
// =============================================================================
//
// This page used to be a mockup: seven cards, each with a blue "Connect"
// button, none of which had an onClick. SAM.gov was labelled "connected" with a
// "Configure" button that did nothing, and Zapier, Slack, Teams, Salesforce and
// SeaPort NxG had no backend behind them at all. A visitor could click every
// button on the page and nothing would happen anywhere.
//
// The roadmap items are worth showing — people choose products on where they
// are going. What is not worth showing is a button that lies about being ready.
// So: things you can use carry a control that works, and things we intend to
// build are listed as intentions, without one.

const LIVE = [
  {
    name: 'SAM.gov',
    description:
      'Federal solicitations, sync automatically. Nothing to connect — it is how opportunity search works.',
    icon: BuildingLibraryIcon,
    iconBg: 'bg-blue-50',
    iconFg: 'text-blue-600',
    action: { label: 'Browse opportunities', to: '/opportunities' },
  },
];

// Rendered next to LIVE rather than in it: it has two actions and its own
// emphasis, but it is working today, not in development.
const AGENT_ACCESS = [
  {
    name: 'AI Agents (MCP)',
    description:
      'Let Claude, ChatGPT, Gemini or your own code read your pipeline directly. Sign in from the app — read-only, and you approve what it sees.',
    icon: CpuChipIcon,
    iconBg: 'bg-purple-50',
    iconFg: 'text-purple-600',
    actions: [
      { label: 'Connect an app', to: '/settings?tab=api-keys' },
      { label: 'What is coming', to: '/agents', external: true },
    ],
  },
];

// No backend, no UI, no date. Listed as direction, not as product.
const PLANNED = [
  { name: 'GSA eBuy', description: 'GSA schedule opportunities' },
  { name: 'SeaPort NxG', description: 'Navy contract vehicle' },
  { name: 'Slack', description: 'Pipeline alerts and deadline reminders' },
  { name: 'Microsoft Teams', description: 'Team notifications and bid updates' },
  { name: 'Zapier', description: 'Push Clevor events into your own tools' },
  { name: 'Salesforce', description: 'CRM sync' },
];

const Integrations: React.FC = () => {
  return (
    <div className="space-y-8">
      <PageHeader
        title="Integrations"
        subtitle="Where Clevor connects to the rest of your tools"
      />

      {/* ------------------------------------------------------------ */}
      {/* Working today                                                 */}
      {/* ------------------------------------------------------------ */}
      <section>
        <h2 className="mb-4 text-title text-content">Working today</h2>
        <div className="grid grid-cols-1 gap-6 md:grid-cols-2 lg:grid-cols-3">
          {LIVE.map((item) => (
            <div key={item.name} className="rounded-lg bg-surface p-6 shadow">
              <div className="flex items-start justify-between">
                <div className={`flex h-12 w-12 items-center justify-center rounded-xl ${item.iconBg}`}>
                  <item.icon className={`h-6 w-6 ${item.iconFg}`} />
                </div>
                <span className="rounded-full bg-green-100 px-2 py-1 text-xs font-medium text-green-800">
                  Built in
                </span>
              </div>
              <h3 className="mt-4 text-title text-content">{item.name}</h3>
              <p className="mt-1 text-sm text-content-muted">{item.description}</p>
              <Link
                to={item.action.to}
                className="mt-4 block w-full rounded-md bg-surface-sunk px-4 py-2 text-center text-sm font-medium text-content transition-colors hover:bg-gray-200"
              >
                {item.action.label}
              </Link>
            </div>
          ))}

          {AGENT_ACCESS.map((item) => (
            <div
              key={item.name}
              className="rounded-lg bg-surface p-6 shadow ring-1 ring-purple-200"
            >
              <div className="flex items-start justify-between">
                <div className={`flex h-12 w-12 items-center justify-center rounded-xl ${item.iconBg}`}>
                  <item.icon className={`h-6 w-6 ${item.iconFg}`} />
                </div>
                <span className="rounded-full bg-green-100 px-2 py-1 text-xs font-medium text-green-800">
                  Read-only
                </span>
              </div>
              <h3 className="mt-4 text-title text-content">{item.name}</h3>
              <p className="mt-1 text-sm text-content-muted">{item.description}</p>
              <div className="mt-4 space-y-2">
                {item.actions.map((action, i) => (
                  <Link
                    key={action.label}
                    to={action.to}
                    className={`flex w-full items-center justify-center gap-1.5 rounded-md px-4 py-2 text-sm font-medium transition-colors ${
                      i === 0
                        ? 'bg-brand-600 text-white hover:bg-brand-700'
                        : 'bg-surface-sunk text-content hover:bg-gray-200'
                    }`}
                  >
                    {action.label}
                    {'external' in action && action.external && (
                      <ArrowTopRightOnSquareIcon className="h-3.5 w-3.5" />
                    )}
                  </Link>
                ))}
              </div>
            </div>
          ))}
        </div>
      </section>

      {/* ------------------------------------------------------------ */}
      {/* Roadmap                                                       */}
      {/* ------------------------------------------------------------ */}
      <section>
        <h2 className="text-title text-content">On the roadmap</h2>
        <p className="mt-1 text-sm text-content-muted">
          Not built yet, and no dates. If one of these is what would make Clevor work for you,
          tell us at{' '}
          <a href="mailto:support@clevor.ai" className="font-medium text-brand-600 hover:underline">
            support@clevor.ai
          </a>{' '}
          — it moves things up the list.
        </p>
        <div className="mt-4 grid grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-3">
          {PLANNED.map((item) => (
            <div
              key={item.name}
              className="rounded-lg border border-dashed border-border-strong px-4 py-3"
            >
              <p className="text-sm font-medium text-content">{item.name}</p>
              <p className="mt-0.5 text-xs text-content-subtle">{item.description}</p>
            </div>
          ))}
        </div>
      </section>
    </div>
  );
};

export default Integrations;
